/**
 * Comentarios y observaciones de un documento: el tipo que devuelve el backend
 * y la lista con la que se pintan.
 *
 * Vive aparte porque lo consumen dos pantallas —el visor documental y el
 * listado de documentos de una solicitud— y el comentario tiene que verse
 * igual en las dos. Mientras solo existió dentro del visor, quien comentaba
 * desde el listado guardaba bien y no volvía a ver su comentario nunca.
 */

/** Vista de lectura de `kernel.observacion_documento`. */
export interface ObservacionResumen {
  id: string;
  versionDocumentoId: string;
  documentoId: string | null;
  tipo: "PREVENCION" | "COMENTARIO" | "RECHAZO";
  requisitoCodigo: string | null;
  texto: string;
  accionEsperada: string | null;
  observadoPor: string | null;
  /** Nombre completo de quien observó; null si el usuario ya no está en el catálogo. */
  observadoPorNombre: string | null;
  fechaObservacion: string;
  resuelto: boolean;
  resueltoPor: string | null;
  fechaResolucion: string | null;
  nuevaVersionId: string | null;
}

/** Fecha y hora locales: un comentario se identifica por el minuto en que se hizo. */
export function formatFechaHora(s: string | null | undefined): string {
  if (!s) return "—";
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleString("es-MX", {
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
  });
}

/** Autoría legible: nombre si el backend lo resolvió, si no una nota honesta. */
export function autorDe(o: ObservacionResumen): string {
  return o.observadoPorNombre?.trim() || "Usuario no disponible";
}

/**
 * Hilo de comentarios de un documento, del más reciente al más antiguo.
 * Cada renglón dice qué se comentó, quién lo escribió y cuándo.
 */
export function ListaComentarios({
  observaciones, loading, vacio = "Sin comentarios sobre este documento.",
}: {
  observaciones: ObservacionResumen[];
  loading?: boolean;
  vacio?: string;
}) {
  if (loading) return <p className="p-3 text-center text-xs text-fg-subtle">Cargando comentarios…</p>;
  if (observaciones.length === 0) return <p className="p-3 text-center text-xs text-fg-subtle">{vacio}</p>;
  return (
    <ul className="divide-y">
      {observaciones.map((o) => (
        <li key={o.id} className="p-3">
          <div className="flex items-center justify-between gap-2 text-xs">
            <EtiquetaTipo tipo={o.tipo} />
            <span className="text-[11px] text-fg-subtle">{formatFechaHora(o.fechaObservacion)}</span>
          </div>
          <p className="mt-1 whitespace-pre-wrap text-sm text-fg">{o.texto}</p>
          <p className="mt-1 text-[11px] text-fg-subtle">Registrado por {autorDe(o)}</p>
          {o.accionEsperada && (
            <p className="mt-1 text-xs text-fg-muted">
              <span className="font-medium">Acción esperada:</span> {o.accionEsperada}
            </p>
          )}
          {o.resuelto && (
            <p className="mt-1 text-[11px] text-estado-ok">
              Resuelta el {formatFechaHora(o.fechaResolucion)}
              {o.nuevaVersionId ? " — con nueva versión" : ""}
            </p>
          )}
        </li>
      ))}
    </ul>
  );
}

function EtiquetaTipo({ tipo }: { tipo: ObservacionResumen["tipo"] }) {
  const estilo =
    tipo === "PREVENCION" ? "bg-estado-warn-bg text-estado-warn"
    : tipo === "RECHAZO" ? "bg-estado-err-bg text-estado-err"
    : "bg-estado-info-bg text-estado-info";
  return <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${estilo}`}>{tipo.toLowerCase()}</span>;
}
